import { recomputeParking, recomputeSlopeCheck, scheduleSave, state } from './app';
import {
  getBuildingScreenCorners,
  getHandleScreenPositions,
  HANDLE_HIT_R,
  redrawBuildingsOverlay,
} from './buildingRenderer';
import { addP, pointInPolygon, rotP, subP, toRad } from './geometry';
import { getLeaflet, map, screenToLm } from './mapView';
import {
  BICYCLE_ZONE_ID,
  isParkingZoneId,
  PARKING_METHOD_KEYS,
  parkingZoneId,
  parkingZoneKeyFromId,
} from './parking/constants';
import type { OrientedRect, Point, SelectableRect } from './types';
import { highlightSelectedRow, renderSidebar, updateLegalSummary } from './ui/sidebar';

// Building/zone hit-testing, drag/resize/rotate interaction. getBuilding()
// is the polymorphic lookup shared across buildings, parking-method zones,
// and the bicycle zone, keyed by string id prefix.

export function getBuilding(id: string | null): SelectableRect | null | undefined {
  if (id === BICYCLE_ZONE_ID) return state.bicycle.enabled ? state.bicycle.zone : null;
  if (id && isParkingZoneId(id)) {
    const m = state.parking.methods[parkingZoneKeyFromId(id)];
    return m?.enabled ? m.zone : null;
  }
  return state.buildings.find((b) => b.id === id);
}
export function getEditableZones(): { id: string; rect: OrientedRect }[] {
  const zones: { id: string; rect: OrientedRect }[] = [];
  PARKING_METHOD_KEYS.forEach((key) => {
    if (key === 'backToBack') return;
    const m = state.parking.methods[key];
    if (m.enabled && m.zone) zones.push({ id: parkingZoneId(key), rect: m.zone });
  });
  if (state.bicycle.enabled && state.bicycle.zone)
    zones.push({ id: BICYCLE_ZONE_ID, rect: state.bicycle.zone });
  return zones;
}

let redrawScheduled = false;
function throttledRedraw(): void {
  if (redrawScheduled) return;
  redrawScheduled = true;
  requestAnimationFrame(() => {
    redrawScheduled = false;
    redrawBuildingsOverlay();
    updateLegalSummary();
  });
}

function hitTestHandles(b: OrientedRect, pt: Point): HandleKey | null {
  const h = getHandleScreenPositions(b);
  const order: HandleKey[] = ['rotate', 'nw', 'ne', 'se', 'sw', 'n', 'e', 's', 'w'];
  for (const key of order) {
    const p = h[key];
    if (Math.hypot(pt.x - p.x, pt.y - p.y) <= HANDLE_HIT_R) return key;
  }
  return null;
}
function pointInBuildingScreen(b: OrientedRect, pt: Point): boolean {
  return pointInPolygon(pt, getBuildingScreenCorners(b));
}

type ResizeHandle = 'nw' | 'ne' | 'se' | 'sw' | 'n' | 'e' | 's' | 'w';
type HandleKey = ResizeHandle | 'rotate';

type DragState =
  | { type: 'move'; buildingId: string; dragStartLm: Point; startCenter: Point }
  | {
      type: 'resize';
      buildingId: string;
      handleType: ResizeHandle;
      anchorLm: Point;
      startAngle: number;
    }
  | { type: 'rotate'; buildingId: string };

let dragState: DragState | null = null;

function startMoveDrag(id: string, b: OrientedRect, screenPt: Point): void {
  dragState = {
    type: 'move',
    buildingId: id,
    dragStartLm: screenToLm(screenPt),
    startCenter: { ...b.center },
  };
}
function startHandleDrag(
  id: string,
  b: OrientedRect,
  handleType: HandleKey,
  _screenPt: Point
): void {
  if (handleType === 'rotate') {
    dragState = { type: 'rotate', buildingId: id };
    return;
  }
  const w = b.width / 2;
  const d = b.depth / 2;
  // ドラッグするハンドルの対角(角ハンドル)/対辺(辺ハンドル)を固定点(アンカー)として覚えておく。
  // リサイズ中はこのアンカーを基準に幅・奥行き・中心位置を再計算する(doResize参照)
  const oppositeMap: Record<ResizeHandle, Point> = {
    nw: { x: w, y: d },
    ne: { x: -w, y: d },
    se: { x: -w, y: -d },
    sw: { x: w, y: -d },
    n: { x: 0, y: d },
    s: { x: 0, y: -d },
    e: { x: -w, y: 0 },
    w: { x: w, y: 0 },
  };
  const anchorLocal = oppositeMap[handleType];
  const anchorLm = addP(rotP(anchorLocal, b.angle), b.center);
  dragState = { type: 'resize', buildingId: id, handleType, anchorLm, startAngle: b.angle };
}

function doResize(ds: Extract<DragState, { type: 'resize' }>, nowLm: Point): void {
  const b = getBuilding(ds.buildingId);
  if (!b) return;
  const MIN = 2;
  const d = rotP(subP(nowLm, ds.anchorLm), -ds.startAngle);
  switch (ds.handleType) {
    case 'nw':
    case 'ne':
    case 'se':
    case 'sw': {
      const newWidth = Math.max(MIN, Math.abs(d.x));
      const newDepth = Math.max(MIN, Math.abs(d.y));
      b.width = newWidth;
      b.depth = newDepth;
      b.center = addP(ds.anchorLm, rotP({ x: d.x / 2, y: d.y / 2 }, ds.startAngle));
      break;
    }
    case 'e': {
      const newWidth = Math.max(MIN, d.x);
      b.width = newWidth;
      b.center = addP(ds.anchorLm, rotP({ x: newWidth / 2, y: 0 }, ds.startAngle));
      break;
    }
    case 'w': {
      const newWidth = Math.max(MIN, -d.x);
      b.width = newWidth;
      b.center = addP(ds.anchorLm, rotP({ x: -newWidth / 2, y: 0 }, ds.startAngle));
      break;
    }
    case 's': {
      const newDepth = Math.max(MIN, d.y);
      b.depth = newDepth;
      b.center = addP(ds.anchorLm, rotP({ x: 0, y: newDepth / 2 }, ds.startAngle));
      break;
    }
    case 'n': {
      const newDepth = Math.max(MIN, -d.y);
      b.depth = newDepth;
      b.center = addP(ds.anchorLm, rotP({ x: 0, y: -newDepth / 2 }, ds.startAngle));
      break;
    }
  }
}

export function normalizeAngle(a: number): number {
  const twoPi = Math.PI * 2;
  let r = a % twoPi;
  if (r < 0) r += twoPi;
  return r;
}
function doRotate(
  ds: Extract<DragState, { type: 'rotate' }>,
  nowLm: Point,
  shiftKey: boolean | undefined
): void {
  const b = getBuilding(ds.buildingId);
  if (!b) return;
  // 回転ハンドルは建物の「北(角度0)」側に立っているため、中心からマウス位置への角度は
  // 建物の実際の回転角より90度ずれる。+Math.PI/2でその分を補正する
  let angle = Math.atan2(nowLm.y - b.center.y, nowLm.x - b.center.x) + Math.PI / 2;
  if (shiftKey) {
    const step = toRad(15);
    angle = Math.round(angle / step) * step;
  }
  b.angle = normalizeAngle(angle);
}

const HANDLE_CURSORS: Record<HandleKey, string> = {
  nw: 'nwse-resize',
  se: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  rotate: 'grab',
};
function setMapCursor(cursor: string): void {
  if (map) map.getContainer().style.cursor = cursor || '';
}
function updateHoverCursor(pt: Point): void {
  if (!map || dragState) return;
  if (!state.site.origin || state.ui.mode === 'site-edit') {
    setMapCursor('');
    return;
  }
  const sel = state.ui.selectedId ? getBuilding(state.ui.selectedId) : null;
  if (sel) {
    const hit = hitTestHandles(sel, pt);
    if (hit) {
      setMapCursor(HANDLE_CURSORS[hit] || 'pointer');
      return;
    }
  }
  for (let i = state.buildings.length - 1; i >= 0; i--) {
    if (pointInBuildingScreen(state.buildings[i], pt)) {
      setMapCursor('move');
      return;
    }
  }
  for (const z of getEditableZones()) {
    if (pointInBuildingScreen(z.rect, pt)) {
      setMapCursor('move');
      return;
    }
  }
  setMapCursor('');
}

// biome-ignore lint/suspicious/noExplicitAny: Leaflet mouse event
export function onMapMouseDown(e: any): void {
  if (!state.site.origin || state.ui.mode === 'site-edit') return;
  const pt: Point = e.containerPoint;
  const sel = state.ui.selectedId ? getBuilding(state.ui.selectedId) : null;
  const domEvent = getLeaflet().DomEvent;
  if (sel) {
    const hit = hitTestHandles(sel, pt);
    if (hit) {
      domEvent.stop(e);
      map?.dragging.disable();
      startHandleDrag(state.ui.selectedId as string, sel, hit, pt);
      setMapCursor(hit === 'rotate' ? 'grabbing' : HANDLE_CURSORS[hit] || 'pointer');
      return;
    }
  }
  for (let i = state.buildings.length - 1; i >= 0; i--) {
    const b = state.buildings[i];
    if (pointInBuildingScreen(b, pt)) {
      domEvent.stop(e);
      map?.dragging.disable();
      state.ui.selectedId = b.id;
      startMoveDrag(b.id, b, pt);
      setMapCursor('move');
      redrawBuildingsOverlay();
      highlightSelectedRow();
      return;
    }
  }
  for (const z of getEditableZones()) {
    if (pointInBuildingScreen(z.rect, pt)) {
      domEvent.stop(e);
      map?.dragging.disable();
      state.ui.selectedId = z.id;
      startMoveDrag(z.id, z.rect, pt);
      setMapCursor('move');
      redrawBuildingsOverlay();
      highlightSelectedRow();
      return;
    }
  }
  if (state.ui.selectedId) {
    state.ui.selectedId = null;
    redrawBuildingsOverlay();
    highlightSelectedRow();
  }
}
// biome-ignore lint/suspicious/noExplicitAny: Leaflet mouse event
export function onMapMouseMove(e: any): void {
  if (!dragState) {
    updateHoverCursor(e.containerPoint);
    return;
  }
  getLeaflet().DomEvent.stop(e);
  const nowLm = screenToLm(e.containerPoint);
  if (dragState.type === 'move') {
    const b = getBuilding(dragState.buildingId);
    if (b) b.center = addP(dragState.startCenter, subP(nowLm, dragState.dragStartLm));
  } else if (dragState.type === 'resize') {
    doResize(dragState, nowLm);
  } else if (dragState.type === 'rotate') {
    doRotate(dragState, nowLm, e.originalEvent?.shiftKey);
  }
  throttledRedraw();
}
// biome-ignore lint/suspicious/noExplicitAny: Leaflet mouse event
export function onMapMouseUp(e: any): void {
  if (!dragState) return;
  const wasEditingBicycleZone = dragState.buildingId === BICYCLE_ZONE_ID;
  dragState = null;
  map?.dragging.enable();
  if (wasEditingBicycleZone) state.bicycle.autoZone = false;
  recomputeSlopeCheck();
  recomputeParking();
  redrawBuildingsOverlay();
  renderSidebar();
  scheduleSave();
  updateHoverCursor(e.containerPoint);
}
